// 1.3.44: 集成 Ant Design X - 使用 XMarkdown 替换 react-markdown
// 解决格式不稳定问题：列表序号重复、多个独立的 <ol> 标签问题

import { memo, lazy, Suspense } from 'react';
import { XMarkdown } from '@ant-design/x-markdown';
// 1.4.1: 测试环境避免直接引入 CodeHighlighter（ESM/CJS 兼容问题）
// import { CodeHighlighter } from '@ant-design/x'; // 1.3.44: 使用 CodeHighlighter 进行代码高亮
import { motion } from 'framer-motion';
import type { ComponentProps } from '@ant-design/x-markdown';

interface MarkdownRendererProps {
  content: string;
  isStreaming?: boolean;
}

/**
 * 1.3.44: MarkdownRenderer - 使用 XMarkdown 优化流式渲染
 * 
 * 改进：
 * - ✅ 使用 XMarkdown 专为流式内容优化
 * - ✅ 自动处理流式列表，无需 DOM 修复逻辑
 * - ✅ 更稳定的流式渲染
 * - ✅ 移除约 200 行手动解析和修复代码
 */
// 1.4.1: 测试环境避免加载 CodeHighlighter
const isTestEnv = import.meta.env.MODE === 'test';
const LazyCodeHighlighter = isTestEnv
  ? null
  : lazy(async () => {
    const module = await import('@ant-design/x');
    return { default: module.CodeHighlighter };
  });

export const MarkdownRenderer = memo(function MarkdownRenderer({
  content,
  isStreaming = false
}: MarkdownRendererProps) {
  return (
    <div className="markdown-content">
      <XMarkdown
        content={content}
        streaming={{
          hasNextChunk: isStreaming,
          enableAnimation: false, // 禁用动画以保持性能
        }}
        openLinksInNewTab={true}
        components={{
          // 段落样式 - 增加间距以便餐厅名称和描述之间有清晰分隔
          p: ({ children, domNode, streamStatus, ...props }: ComponentProps) => (
            <p className="mb-3 last:mb-0 leading-relaxed break-words" {...props}>
              {children}
            </p>
          ),
          // 粗体样式 - 确保餐厅名称有足够的视觉权重和大小
          strong: ({ children, domNode, streamStatus, ...props }: ComponentProps) => (
            <strong className="font-bold text-gray-900 text-base xs:text-lg" {...props}>
              {children}
            </strong>
          ),
          // 斜体样式
          em: ({ children, domNode, streamStatus, ...props }: ComponentProps) => (
            <em className="italic text-gray-700" {...props}>
              {children}
            </em>
          ),
          // 无序列表
          ul: ({ children, domNode, streamStatus, ...props }: ComponentProps) => (
            <ul className="list-disc list-outside mb-2 space-y-1.5 ml-4 pl-1" {...props}>
              {children}
            </ul>
          ),
          // 有序列表 - XMarkdown 自动处理流式列表，无需手动修复
          ol: ({ children, domNode, streamStatus, ...props }: ComponentProps) => (
            <ol 
              className="list-decimal list-outside mb-2 ml-6 pl-0" 
              style={{ 
                listStyleType: 'decimal',
                listStylePosition: 'outside',
                paddingLeft: '0',
                marginLeft: '1.5rem',
              }} 
              {...props}
            >
              {children}
            </ol>
          ),
          // 列表项
          li: ({ children, domNode, streamStatus, ...props }: ComponentProps) => (
            <li 
              className="text-sm xs:text-base leading-relaxed mb-1.5" 
              style={{ 
                display: 'list-item',
                listStyleType: 'decimal',
                listStylePosition: 'outside',
                paddingLeft: '0.25rem',
                marginLeft: '0',
              }} 
              {...props}
            >
              {children}
            </li>
          ),
          // 1.3.44: 代码块 - 使用 CodeHighlighter 进行语法高亮
          // 内联代码保持原样
          code: ({ children, domNode, streamStatus, ...props }: ComponentProps & { inline?: boolean }) => {
            const inline = (props as any).inline;
            if (inline) {
              return (
                <code className="px-1.5 py-0.5 bg-gray-200 rounded text-sm font-mono text-orange-600" {...props}>
                  {children}
                </code>
              );
            }
            // 块级代码会在 pre 组件中处理，这里返回 null 或原样渲染
            return (
              <code className="block p-3 bg-gray-100 rounded-lg text-sm font-mono overflow-x-auto" {...props}>
                {children}
              </code>
            );
          },
          // 1.3.44: 代码块容器 - 使用 CodeHighlighter 替换
          // 1.4.1: 测试环境使用 fallback，避免 ESM/CJS 兼容问题
          pre: ({ children, domNode, streamStatus, ...props }: ComponentProps) => {
            // 从 domNode 中提取代码内容和语言
            let codeInfo: { lang: string; codeContent: string } | null = null;

            if (domNode && typeof domNode === 'object' && 'children' in domNode) {
              // 查找 code 子元素
              const codeNode = (domNode as any).children?.find(
                (child: any) => child && typeof child === 'object' && 'name' in child && child.name === 'code'
              );

              if (codeNode) {
                // 提取语言（从 class 属性，如 "language-javascript"）
                const className = codeNode.attribs?.class || '';
                const langMatch = className.match(/language-(\w+)/);
                const lang = langMatch ? langMatch[1] : '';

                // 提取代码内容（从文本节点）
                const extractText = (node: any): string => {
                  if (!node) return '';
                  if (typeof node === 'string') return node;
                  if (node.type === 'text') return node.data || '';
                  if (Array.isArray(node.children)) {
                    return node.children.map(extractText).join('');
                  }
                  return '';
                };

                const codeContent = extractText(codeNode);

                if (codeContent) {
                  codeInfo = { lang, codeContent };
                }
              }
            }

            const fallbackBlock = (
              <pre className="mb-2 overflow-x-auto" {...props}>
                {codeInfo?.codeContent || children}
              </pre>
            );

            // 1.4.1: 使用 LazyCodeHighlighter（非测试环境）
            if (codeInfo && codeInfo.codeContent && LazyCodeHighlighter) {
              return (
                <div className="mb-2">
                  <Suspense fallback={fallbackBlock}>
                    <LazyCodeHighlighter lang={codeInfo.lang || undefined}>
                      {codeInfo.codeContent}
                    </LazyCodeHighlighter>
                  </Suspense>
                </div>
              );
            }

            // 回退到原始渲染（如果没有提取到代码信息，可能是内联代码或其他情况）
            // 注意：children 可能是 React 元素，需要渲染
            return fallbackBlock;
          },
          // 标题
          h1: ({ children, ...props }: ComponentProps) => (
            <h1 className="text-xl font-bold mb-2 mt-3 first:mt-0" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }: ComponentProps) => (
            <h2 className="text-lg font-semibold mb-2 mt-3 first:mt-0" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }: ComponentProps) => (
            <h3 className="text-base font-semibold mb-2 mt-2 first:mt-0" {...props}>
              {children}
            </h3>
          ),
          // 链接
          a: ({ children, ...props }: ComponentProps & { href?: string }) => (
            <a
              href={(props as any).href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
              {...props}
            >
              {children}
            </a>
          ),
          // 引用
          blockquote: ({ children, ...props }: ComponentProps) => (
            <blockquote className="border-l-4 border-orange-300 pl-3 py-1 my-2 bg-orange-50 italic" {...props}>
              {children}
            </blockquote>
          ),
          // 水平线
          hr: (props: ComponentProps) => <hr className="my-3 border-gray-300" {...props} />,
        }}
      />
      {isStreaming && (
        <motion.span
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="inline-block w-1.5 xs:w-2 h-3 xs:h-4 bg-orange-500 ml-1"
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if content or streaming status changes
  return prevProps.content === nextProps.content && 
         prevProps.isStreaming === nextProps.isStreaming;
});

